import type { ChatUiMessage } from '@/hooks/useChat';
import { cn } from '@/lib/utils';
import { isGerman } from '@/i18n/locale';

interface Props {
  message: ChatUiMessage;
  streaming?: boolean;
}

// Font rendering for AI bubbles is tuned for token-by-token streaming.
// Sans-serif (Inter via font-sans), antialiased, tabular numbers, and
// subtle negative letter-spacing make tokens land smoothly instead of
// the line jumping as each chunk arrives — closer to how ChatGPT/Claude
// render their stream. Explicit font-sans guarantees no display serif
// (Lora) bleeds in from any parent.
const AI_TEXT_CLASSES =
  "font-sans antialiased text-[15px] leading-[1.55] tracking-[-0.003em] tabular-nums [font-feature-settings:'cv11','ss01']";

const USER_TEXT_CLASSES = 'font-sans antialiased text-[15px] leading-[1.55]';

export function MessageBubble({ message, streaming }: Props) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[88%] rounded-2xl px-4 py-3',
          isUser
            ? 'bg-primary text-white rounded-br-md'
            : 'bg-white border border-primary-50 rounded-bl-md shadow-sm'
        )}
      >
        <div
          className={cn(
            'whitespace-pre-wrap',
            isUser ? USER_TEXT_CLASSES : AI_TEXT_CLASSES,
            streaming && 'streaming-cursor'
          )}
        >
          {message.content}
        </div>
        {!isUser && message.intent && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-muted/80">
            <IntentBadge intent={message.intent} />
          </div>
        )}
      </div>
    </div>
  );
}

function IntentBadge({ intent }: { intent: string }) {
  const labels: Record<string, string> = isGerman()
    ? {
        feeding: 'Ernährung',
        sleep: 'Schlaf',
        health: 'Gesundheit',
        development: 'Meilensteine',
        mood: 'Für dich',
        general: '',
      }
    : {
        feeding: 'feeding',
        sleep: 'sleep',
        health: 'health',
        development: 'milestones',
        mood: 'support',
        general: '',
      };
  if (!labels[intent]) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
      {labels[intent]}
    </span>
  );
}
